import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import { Box, Stack, Tooltip, Typography } from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { useQuery } from "@tanstack/react-query"
import i18next from "i18next"
import { useState } from "react"

import DataTableToolbar from "@components/data_table_toolbar"
import GridTable from "@components/grid-table"
import {
  GRAY_COLOR,
  GRID_HEADER_ITEM,
  PRIMARY,
  WATSAPP_ACTIVE
} from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import { listSubscribers } from "@domain/changelog"
import type { ChangelogSubscriber, ChangelogPrefs } from "@models/changelog"
import { formatDate } from "@utils/functions/formatDate"

// Channel column shows logos, not text. Push (bell) covers web + mobile.
const CHANNEL_ICONS: {
  key: "email" | "whatsapp" | "push"
  labelKey: string
  Icon: typeof MailOutlineIcon
  color: string
  isActive: (prefs?: ChangelogPrefs) => boolean
}[] = [
  {
    key: "email",
    labelKey: "changelog_channel_email",
    Icon: MailOutlineIcon,
    color: GRAY_COLOR,
    isActive: (prefs) => prefs?.email !== false
  },
  {
    key: "whatsapp",
    labelKey: "changelog_channel_whatsapp",
    Icon: ChatBubbleOutlineIcon,
    color: WATSAPP_ACTIVE,
    isActive: (prefs) => prefs?.whatsapp !== false
  },
  {
    key: "push",
    labelKey: "changelog_channel_push",
    Icon: NotificationsNoneIcon,
    color: PRIMARY,
    isActive: (prefs) => prefs?.web !== false || prefs?.mobile !== false
  }
]

export default function SubscribersSection() {
  const { translate } = useChangelogPageContext()
  const [search, setSearch] = useState("")
  const [pagination, setPagination] = useState({ page: 0, pageSize: 20 })

  const handleSearch = (value: string) => {
    setSearch(value)
    setPagination((prev) => ({ ...prev, page: 0 }))
  }

  const { data, isLoading } = useQuery({
    queryKey: ["changelog-subscribers", search, pagination],
    queryFn: async () => {
      const response = await listSubscribers({
        search: search.trim() || undefined,
        pageNumber: pagination.page + 1,
        pageSize: pagination.pageSize
      })
      return response.data
    }
  })

  const subscribers: ChangelogSubscriber[] = data?.docs ?? []
  const totalDocs = data?.totalDocs ?? 0

  const columns: GridColDef[] = [
    {
      field: "person",
      flex: 2,
      minWidth: 180,
      headerName: translate("changelog_col_person"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_person")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ChangelogSubscriber
        const fullName =
          [row.firstName, row.lastName].filter(Boolean).join(" ") || "—"
        return <Box>{fullName}</Box>
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "customer",
      flex: 1.5,
      minWidth: 160,
      headerName: translate("changelog_col_customer"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_customer")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ color: "text.secondary" }}>
          {(params.row as ChangelogSubscriber).customer?.companyName || "—"}
        </Box>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "email",
      flex: 2,
      minWidth: 180,
      headerName: translate("changelog_col_email"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_email")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ color: "text.secondary" }}>
          {(params.row as ChangelogSubscriber).email || "—"}
        </Box>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "phone",
      flex: 1.5,
      minWidth: 150,
      headerName: translate("changelog_col_phone"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_phone")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ color: "text.secondary" }}>
          {(params.row as ChangelogSubscriber).phone || "—"}
        </Box>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "channels",
      flex: 1.5,
      minWidth: 160,
      headerName: translate("changelog_col_channels"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_channels")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const prefs = (params.row as ChangelogSubscriber).prefs
        const active = CHANNEL_ICONS.filter((channel) =>
          channel.isActive(prefs)
        )
        if (!active.length) {
          return <Box sx={{ color: GRAY_COLOR }}>—</Box>
        }
        return (
          <Stack direction="row" gap={1} alignItems="center">
            {active.map(({ key, labelKey, Icon, color }) => (
              <Tooltip key={key} title={translate(labelKey)}>
                <Icon sx={{ fontSize: "1.15rem", color }} />
              </Tooltip>
            ))}
          </Stack>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "createdAt",
      flex: 1,
      minWidth: 140,
      headerName: translate("changelog_col_subscribed_at"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_subscribed_at")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value = (params.row as ChangelogSubscriber).createdAt
        return (
          <Box sx={{ color: "text.secondary" }}>
            {value ? formatDate(i18next.language, value) : "—"}
          </Box>
        )
      },
      sortable: false,
      disableColumnMenu: true
    }
  ]

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
          {translate("changelog_subscribers")}
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          {translate("changelog_subscribers_subtitle")}
        </Typography>
      </Box>

      <DataTableToolbar onSearch={handleSearch} />
      <GridTable
        loading={isLoading}
        rows={subscribers}
        columns={columns}
        rowCount={totalDocs}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row: ChangelogSubscriber) => row._id}
        disableColumnSelector={true}
        noRowsTableText="changelog_subscribers_empty"
      />
    </Box>
  )
}
